import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Camera,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  UserCheck,
  History,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import { apiFetch } from '../../lib/apiConfig';

interface AdminUserKycDetailsProps {
  userId: string;
  userName?: string;
  userEmail?: string;
  userRole?: 'client' | 'companion' | string;
  onStatusChanged?: () => void;
  onReviewComplete?: () => void;
}

export const AdminUserKycDetailsSection: React.FC<AdminUserKycDetailsProps> = ({
  userId,
  userName,
  userEmail,
  userRole,
  onStatusChanged,
  onReviewComplete,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [selfie, setSelfie] = useState<any | null>(null);
  const [resolvedUser, setResolvedUser] = useState<{
    userId: string;
    userName: string;
    userEmail: string;
    userRole: string;
  }>({
    userId,
    userName: userName || userId,
    userEmail: userEmail || '',
    userRole: userRole || 'client',
  });

  // Secure blob URLs for private KYC previews
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});

  // Per-document and selfie rejection reason state
  const [docReasons, setDocReasons] = useState<Record<string, string>>({});
  const [docErrors, setDocErrors] = useState<Record<string, string>>({});
  const [selfieReason, setSelfieReason] = useState<string>('');
  const [selfieError, setSelfieError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [feedbackBanner, setFeedbackBanner] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const fetchProtectedPreview = useCallback(async (url: string, token: string): Promise<string | null> => {
    try {
      const res = await apiFetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, []);

  const loadKycDetails = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Administrator session required to view KYC documents and selfie.');
        setLoading(false);
        return;
      }
      const token = await currentUser.getIdToken();
      const res = await apiFetch(`/api/admin/kyc/user/${encodeURIComponent(userId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || 'Unable to load user KYC documents and selfie.');
      }

      const docs = Array.isArray(data.documents) ? data.documents : [];
      const selfieData = data.selfie || null;
      setDocuments(docs);
      setSelfie(selfieData);
      if (data.user) {
        setResolvedUser({
          userId: data.user.userId || userId,
          userName: data.user.userName || userName || userId,
          userEmail: data.user.userEmail || userEmail || '',
          userRole: data.user.userRole || userRole || 'client',
        });
      }

      const initialReasons: Record<string, string> = {};
      for (const d of docs) {
        initialReasons[d.id] = d.rejectionReason || '';
      }
      setDocReasons(initialReasons);
      setSelfieReason(selfieData?.rejectionReason || '');

      // Load protected image blobs
      const nextBlobs: Record<string, string> = {};
      for (const d of docs) {
        if (d.frontPreviewUrl) {
          const b = await fetchProtectedPreview(d.frontPreviewUrl, token);
          if (b) nextBlobs[`${d.id}:front`] = b;
        }
        if (d.backPreviewUrl) {
          const b = await fetchProtectedPreview(d.backPreviewUrl, token);
          if (b) nextBlobs[`${d.id}:back`] = b;
        }
      }
      if (selfieData?.selfiePreviewUrl) {
        const b = await fetchProtectedPreview(selfieData.selfiePreviewUrl, token);
        if (b) nextBlobs['selfie'] = b;
      }
      setBlobUrls(nextBlobs);
    } catch (err: any) {
      setError(err.message || 'Failed to load KYC documents and selfie.');
    } finally {
      setLoading(false);
    }
  }, [userId, userName, userEmail, userRole, fetchProtectedPreview]);

  useEffect(() => {
    void loadKycDetails();
  }, [loadKycDetails]);

  const handleReviewDocument = async (docId: string, status: 'VERIFIED' | 'REJECTED') => {
    setDocErrors((prev) => ({ ...prev, [docId]: '' }));
    setFeedbackBanner(null);

    const reason = (docReasons[docId] || '').trim();
    if (status === 'REJECTED' && !reason) {
      setDocErrors((prev) => ({
        ...prev,
        [docId]: 'A Rejection Reason is strictly required before rejecting this document.',
      }));
      return;
    }

    setSubmittingId(docId);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await apiFetch('/api/admin/kyc/review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          documentId: docId,
          userId: resolvedUser.userId,
          reviewTarget: 'DOCUMENT',
          status,
          rejectionReason: status === 'REJECTED' ? reason : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to update document status.');
      }

      setFeedbackBanner({
        type: 'success',
        message:
          status === 'REJECTED'
            ? `Document rejected and notification sent to ${resolvedUser.userName} with reason: "${reason}"`
            : `Document verified for ${resolvedUser.userName}.`,
      });
      await loadKycDetails();
      onStatusChanged?.();
      onReviewComplete?.();
    } catch (err: any) {
      setDocErrors((prev) => ({
        ...prev,
        [docId]: err.message || 'Review action failed.',
      }));
    } finally {
      setSubmittingId(null);
    }
  };

  const handleReviewSelfie = async (status: 'VERIFIED' | 'REJECTED') => {
    setSelfieError(null);
    setFeedbackBanner(null);

    const reason = selfieReason.trim();
    if (status === 'REJECTED' && !reason) {
      setSelfieError('A Rejection Reason is strictly required before rejecting the selfie.');
      return;
    }

    const selfieDocId = selfie?.id || `selfie:${resolvedUser.userId}`;
    setSubmittingId(selfieDocId);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await apiFetch('/api/admin/kyc/review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          documentId: selfieDocId,
          userId: resolvedUser.userId,
          reviewTarget: 'SELFIE',
          status,
          rejectionReason: status === 'REJECTED' ? reason : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to update selfie status.');
      }

      setFeedbackBanner({
        type: 'success',
        message:
          status === 'REJECTED'
            ? `Selfie rejected and notification sent to ${resolvedUser.userName} with reason: "${reason}"`
            : `Selfie verified for ${resolvedUser.userName}.`,
      });
      await loadKycDetails();
      onStatusChanged?.();
      onReviewComplete?.();
    } catch (err: any) {
      setSelfieError(err.message || 'Selfie review failed.');
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="bg-slate-950/90 border border-slate-800 rounded-2xl p-4 sm:p-5 space-y-5">
      {/* User Identity Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-5 h-5 text-indigo-400 shrink-0" />
          <div>
            <h4 className="text-sm font-black text-white flex items-center gap-2 flex-wrap">
              <span>Submitted KYC Documents &amp; Selfie</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 uppercase font-mono">
                {resolvedUser.userRole}: {resolvedUser.userName}
              </span>
            </h4>
            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
              Owner ID: <strong className="text-slate-200">{resolvedUser.userId}</strong>
              {resolvedUser.userEmail ? ` • ${resolvedUser.userEmail}` : ''}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void loadKycDetails()}
          className="px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-indigo-400' : ''}`} />
          <span>Refresh KYC</span>
        </button>
      </div>

      {feedbackBanner && (
        <div
          className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 border ${
            feedbackBanner.type === 'success'
              ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-300'
              : 'bg-rose-950/60 border-rose-700/60 text-rose-300'
          }`}
        >
          {feedbackBanner.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400" />
          )}
          <span>{feedbackBanner.message}</span>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin text-indigo-400" />
          <span>Loading submitted documents and selfie for {resolvedUser.userName}...</span>
        </div>
      ) : error ? (
        <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-800/50 text-xs text-rose-300">
          {error}
        </div>
      ) : (
        <div className="space-y-5">
          {/* 1. Identity Documents List */}
          <div className="space-y-4">
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-indigo-400" />
              <span>Identity Documents ({documents.length})</span>
            </h5>

            {documents.length === 0 ? (
              <div className="p-4 rounded-xl bg-slate-900/70 border border-slate-800 text-xs text-slate-400">
                No identity documents have been uploaded by <strong className="text-slate-200">{resolvedUser.userName}</strong> ({resolvedUser.userId}) yet.
              </div>
            ) : (
              documents.map((doc) => {
                const frontSrc = blobUrls[`${doc.id}:front`];
                const backSrc = blobUrls[`${doc.id}:back`];
                const isSubmitting = submittingId === doc.id;
                const docErr = docErrors[doc.id];

                return (
                  <div
                    key={doc.id}
                    className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black text-white">
                            {doc.requirement?.label || doc.documentType} ({doc.documentType})
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                            Doc ID: {doc.id}
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-950/60 text-indigo-300 border border-indigo-800/50">
                            Belongs to: {resolvedUser.userName} ({resolvedUser.userId})
                          </span>
                        </div>
                        {doc.resubmittedAt && (
                          <p className="text-[11px] text-amber-300 font-semibold mt-1 flex items-center gap-1">
                            <History className="w-3.5 h-3.5" />
                            <span>
                              Resubmitted for review (Submission #{doc.submissionCount || 2}) at{' '}
                              {new Date(doc.resubmittedAt).toLocaleString()}
                            </span>
                          </p>
                        )}
                      </div>

                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold font-mono border ${
                          doc.status === 'VERIFIED'
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                            : doc.status === 'REJECTED'
                            ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                            : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        }`}
                      >
                        {doc.status}
                      </span>
                    </div>

                    {/* Previous Rejection Link Banner if Resubmitted */}
                    {doc.previousRejectionReason && (
                      <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-700/40 text-xs text-amber-200 space-y-1">
                        <div className="font-bold flex items-center gap-1.5 text-amber-300">
                          <History className="w-3.5 h-3.5" />
                          <span>Linked Previous Submission Rejection Reason:</span>
                        </div>
                        <p className="text-slate-200">{doc.previousRejectionReason}</p>
                      </div>
                    )}

                    {/* Active Rejection Reason Banner */}
                    {doc.status === 'REJECTED' && doc.rejectionReason && (
                      <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-700/50 text-xs text-rose-200 space-y-1">
                        <strong className="block text-rose-300">Current Rejection Reason:</strong>
                        <p>{doc.rejectionReason}</p>
                      </div>
                    )}

                    {/* Document Previews */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold">
                          <span>FRONT SIDE — {doc.documentType}</span>
                          <span className="font-mono text-[10px] text-slate-500">{resolvedUser.userName}</span>
                        </div>
                        {frontSrc ? (
                          <img
                            src={frontSrc}
                            alt={`${resolvedUser.userName} ${doc.documentType} Front`}
                            className="w-full h-44 object-contain rounded-lg bg-slate-900 border border-slate-800"
                          />
                        ) : (
                          <div className="h-44 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-xs text-slate-500">
                            Front document stored ({doc.frontPreviewUrl ? 'Protected File' : 'Not uploaded'})
                          </div>
                        )}
                      </div>

                      <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                        <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold">
                          <span>BACK SIDE — {doc.documentType}</span>
                          <span className="font-mono text-[10px] text-slate-500">{resolvedUser.userName}</span>
                        </div>
                        {doc.backPreviewUrl ? (
                          backSrc ? (
                            <img
                              src={backSrc}
                              alt={`${resolvedUser.userName} ${doc.documentType} Back`}
                              className="w-full h-44 object-contain rounded-lg bg-slate-900 border border-slate-800"
                            />
                          ) : (
                            <div className="h-44 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-xs text-slate-500">
                              Back document stored (Protected File)
                            </div>
                          )
                        ) : (
                          <div className="h-44 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-xs text-slate-500">
                            {doc.documentType === 'PAN' ? 'Not required for PAN' : 'No back side uploaded'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin Reject / Approve Controls for this Document */}
                    <div className="pt-2 border-t border-slate-800 space-y-2.5">
                      <label className="block text-xs font-bold text-slate-300">
                        Rejection Reason for {doc.documentType} <span className="text-rose-400">(Mandatory to Reject)</span>
                      </label>
                      <textarea
                        rows={2}
                        value={docReasons[doc.id] || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDocReasons((prev) => ({ ...prev, [doc.id]: val }));
                          if (val.trim()) {
                            setDocErrors((prev) => ({ ...prev, [doc.id]: '' }));
                          }
                        }}
                        placeholder={`Enter exact reason if rejecting ${resolvedUser.userName}'s ${doc.documentType} (e.g. Blurry front image, mismatched ID number)...`}
                        className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                      />
                      {docErr && (
                        <p className="text-xs text-rose-400 font-bold flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                          <span>{docErr}</span>
                        </p>
                      )}
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => void handleReviewDocument(doc.id, 'REJECTED')}
                          className="px-3.5 py-2 rounded-xl bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/40 text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          <span>Reject {doc.documentType}</span>
                        </button>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => void handleReviewDocument(doc.id, 'VERIFIED')}
                          className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Approve {doc.documentType}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* 2. User Selfie Section */}
          <div className="space-y-3 pt-2 border-t border-slate-800">
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-pink-400" />
              <span>Submitted Live Selfie Verification</span>
            </h5>

            <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-black text-white">Selfie Verification Photo</span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-pink-950/60 text-pink-300 border border-pink-800/50">
                      Belongs to: {resolvedUser.userName} ({resolvedUser.userId})
                    </span>
                  </div>
                  {selfie?.resubmittedAt && (
                    <p className="text-[11px] text-amber-300 font-semibold mt-1 flex items-center gap-1">
                      <History className="w-3.5 h-3.5" />
                      <span>
                        Selfie Resubmitted (Submission #{selfie.submissionCount || 2}) at{' '}
                        {new Date(selfie.resubmittedAt).toLocaleString()}
                      </span>
                    </p>
                  )}
                </div>

                <span
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold font-mono border ${
                    selfie?.status === 'VERIFIED'
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                      : selfie?.status === 'REJECTED'
                      ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                      : selfie?.hasSelfie
                      ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                      : 'bg-slate-800 text-slate-400 border-slate-700'
                  }`}
                >
                  {selfie?.status || 'NOT_SUBMITTED'}
                </span>
              </div>

              {selfie?.previousRejectionReason && (
                <div className="p-3 rounded-xl bg-amber-950/30 border border-amber-700/40 text-xs text-amber-200 space-y-1">
                  <div className="font-bold flex items-center gap-1.5 text-amber-300">
                    <History className="w-3.5 h-3.5" />
                    <span>Linked Previous Selfie Rejection Reason:</span>
                  </div>
                  <p className="text-slate-200">{selfie.previousRejectionReason}</p>
                </div>
              )}

              {selfie?.status === 'REJECTED' && selfie?.rejectionReason && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-700/50 text-xs text-rose-200 space-y-1">
                  <strong className="block text-rose-300">Current Selfie Rejection Reason:</strong>
                  <p>{selfie.rejectionReason}</p>
                </div>
              )}

              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold">
                  <span>SELFIE PHOTO — {resolvedUser.userName}</span>
                  <span className="font-mono text-[10px] text-slate-500">ID: {resolvedUser.userId}</span>
                </div>
                {blobUrls['selfie'] ? (
                  <img
                    src={blobUrls['selfie']}
                    alt={`${resolvedUser.userName} Selfie`}
                    className="w-full max-h-56 object-contain rounded-lg bg-slate-900 border border-slate-800"
                  />
                ) : selfie?.hasSelfie ? (
                  <div className="h-40 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-xs text-slate-400">
                    Private Selfie Stored ({selfie.selfieFileKey})
                  </div>
                ) : (
                  <div className="h-32 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-xs text-slate-500">
                    No selfie uploaded by {resolvedUser.userName} yet.
                  </div>
                )}
              </div>

              {selfie?.hasSelfie && (
                <div className="pt-2 border-t border-slate-800 space-y-2.5">
                  <label className="block text-xs font-bold text-slate-300">
                    Rejection Reason for Selfie <span className="text-rose-400">(Mandatory to Reject)</span>
                  </label>
                  <textarea
                    rows={2}
                    value={selfieReason}
                    onChange={(e) => {
                      setSelfieReason(e.target.value);
                      if (e.target.value.trim()) setSelfieError(null);
                    }}
                    placeholder={`Enter exact reason if rejecting ${resolvedUser.userName}'s selfie (e.g. Face not clearly visible, lighting too dark, does not match ID photo)...`}
                    className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-pink-500"
                  />
                  {selfieError && (
                    <p className="text-xs text-rose-400 font-bold flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      <span>{selfieError}</span>
                    </p>
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={submittingId === (selfie?.id || `selfie:${resolvedUser.userId}`)}
                      onClick={() => void handleReviewSelfie('REJECTED')}
                      className="px-3.5 py-2 rounded-xl bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/40 text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      <span>Reject Selfie</span>
                    </button>
                    <button
                      type="button"
                      disabled={submittingId === (selfie?.id || `selfie:${resolvedUser.userId}`)}
                      onClick={() => void handleReviewSelfie('VERIFIED')}
                      className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Approve Selfie</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
